import { getDefaultReceiveMaxHeight } from '../base/environment/videoCapability';
import { setReceiveMaxHeight } from './actions';
// 수신 품질 모니터 — 두 축을 감시해 수신 상한을 한 계단씩 내린다.
//
// 1) 디코드 과부하: (드롭율 > 10%) AND (프레임당 디코드 시간 > 12ms).
//    CPU가 현재 해상도를 못 따라가는 경우. 두 신호를 AND로 묶어
//    탭 스로틀링 등 디코드와 무관한 드롭의 오탐을 배제한다.
// 2) 프리즈 폭증: 5초 창에서 freeze 3회 이상.
//    평균 손실율이 낮아(수 % 미만) BWE가 강등하지 않지만, 버스트성 손실이
//    큰 키프레임을 반복적으로 깨뜨리는 경우(릴레이 경유 1080 수신에서 실측).
//    BWE의 사각지대를 메우며, 해상도보다 부드러움을 우선한다.
const CHECK_INTERVAL_MS = 5000;
const DROP_RATIO_THRESHOLD = 0.1;
// 프레임당 평균 디코드 시간(ms). HW 디코드는 보통 <5ms, SW/과부하는 10ms+.
const DECODE_TIME_MS_THRESHOLD = 12;
// 5초 창당 프리즈 발생 횟수. 정상 스트림은 0에 가깝고, 계층 전환 1회로는 넘지 않는 수준.
const FREEZE_COUNT_THRESHOLD = 3;
// 5초 창당 프리즈 누적 시간(초). 횟수는 적어도 길게 어는 케이스를 잡는다 (창의 20%).
const FREEZE_DURATION_S_THRESHOLD = 1.0;
const MIN_FRAMES_PER_WINDOW = 30;
const STRIKES_TO_DEGRADE = 2;
// 계층 전환 관측(진단 전용): 어떤 스트림이 어떤 경계에서 전환되는지 콘솔에 남긴다.
// 전환은 서버(BWE/JVB 배분) 소관이라 클라이언트가 막을 수 없으므로 강등 근거로 쓰지
// 않는다. (진동 기반 강등을 시도했으나 원인이 외부 클라이언트일 때 선량한 수신자만
// 480까지 내려가는 부작용이 있어 제거 — 대응은 JVB 감쇠 설정으로 한다.)
// 강등 계단(내림차순). 480은 송신 최하단 계층이라 그 이하로는 캡을 걸지 않는다
// (더 낮게 잡으면 받을 계층이 없어 영상이 끊긴다).
const DEGRADE_TIERS = [1080, 720, 480];
const DECODE_FLOOR = 480;
// 마이크로 프리즈 억제: 패킷 틈이 NACK→재전송으로 회수되더라도, 기본 지터버퍼
// 목표(~20-40ms)로는 재전송 도착 전에 재생 시점이 지나가 짧은 프리즈가 된다.
// 목표 지연을 늘려 복구 시간을 벌어준다. 강의는 단방향 시청 위주라 체감 비용이 낮고,
// A/V 동기화를 위해 오디오에도 동일하게 적용한다.
// 250ms 상향: 입장 키프레임(P-프레임의 5~15배, 1080@4M에서 100~250KB)의 전송·디코드
// 공백이 150ms 목표를 넘어 순간 스터터로 보이는 문제를 흡수한다(재전송 회복 여유도 증가).
const JITTER_BUFFER_TARGET_MS = 250;
// (참고) 신규 입장자를 480 계층으로 시작시켜 입장 키프레임 펄스를 낮은 계층에 가두는
// "수신 워밍업"을 시도했으나 제거 — Chrome VP8 시뮬캐스트 인코더는 어느 계층의
// 키프레임 요청이든 전 계층에 동시 생성하므로(강사 outbound keyFramesEncoded 실측)
// 가둬지지 않고, 오히려 상향 시점에 펄스가 한 번 더 생긴다. 입장 1회 = 방 전체 펄스
// 1회가 구조적 바닥이며, 병합·간격 조절은 JVB keyframe.source-wide-min-interval 소관.
// (참고) 위 펄스에서 남는 체감(I-프레임의 거친 양자화가 회복되는 흐릿→선명 램프,
// 입장당 1회 ~0.5초)의 수신측 마스킹을 네 가지로 실측했으나 모두 제거 —
// 인코더가 실제로 화질을 떨어뜨린 구간을 지각 불가능하게 만들 방법은 없었다:
//   ① stats 폴링 후 pause: 감지가 표시보다 늦어(≥100ms) 거친 프레임이 노출된 채 얼음
//   ② 직전 프레임 스냅샷 크로스페이드: 움직임에서 이중 노출(고스팅)
//   ③ CSS 블러 회복: 초점 변화 자체가 도드라짐
//   ④ Encoded Transform으로 디코드 전 감지 → 선명 프레임에서 pause: 기술적으로는
//      완성(키프레임·램프가 화면에 안 그려짐)됐으나, 입장 러시에서 홀드가 연발되어
//      정지가 더 큰 아티팩트가 되고, 전 프레임의 워커 왕복으로 CPU 상시 비용 발생.
// ④의 재사용 노하우: receiver.transform은 RTP가 흐르기 시작한 뒤 붙이면 크로미움이
// 조용히 우회한다(부착·활성까지 되고도 프레임 미통과 실측) — xmpp.session_accept
// 시점(협상 직후, DTLS 완료 전)에 부착해야 실제 경로에 끼워진다. Chrome 128+.
// 결론: 펄스는 수용하고, 입장 러시의 펄스 밀도는 서버(JVB)
// keyframe.source-wide-min-interval(2~3s)로 병합한다.
let timer = null;
let prevDecoded = 0;
let prevDropped = 0;
let prevDecodeTime = 0;
let prevFreezes = 0;
let prevFreezeDuration = 0;
let decodeStrikes = 0;
let freezeStrikes = 0;
let prevHeights = new Map();
/**
 * 수신 품질 감시 안전망 (모든 수신자).
 * 디코드 과부하(CPU)와 프리즈 폭증(버스트 손실) 어느 쪽이든 지속되면
 * 수신 상한을 한 계단씩 480까지 내린다(1080→720→480).
 * 강등은 하향 전용이며 복귀하지 않는다(화질 진동 방지).
 * @param {Object} conference - JitsiConference 인스턴스
 */
export function startReceiveQualityMonitor(conference) {
    stopReceiveQualityMonitor();
    prevDecoded = 0;
    prevDropped = 0;
    prevDecodeTime = 0;
    prevFreezes = 0;
    prevFreezeDuration = 0;
    decodeStrikes = 0;
    freezeStrikes = 0;
    prevHeights = new Map();
    timer = setInterval(() => _checkReceiveHealth(conference), CHECK_INTERVAL_MS);
}
export function stopReceiveQualityMonitor() {
    if (timer) {
        clearInterval(timer);
        timer = null;
    }
}
// 이미 적용한 receiver 추적 (재적용 방지)
const _jitterTargetApplied = new WeakSet();
/**
 * 모든 수신 트랙에 지터버퍼 목표 지연을 적용한다.
 * receiver는 트랙 추가/재협상으로 늘어나므로 감시 주기마다 새 receiver에 적용.
 * @param {RTCPeerConnection} pc
 */
function _applyJitterBufferTarget(pc) {
    const receivers = pc.getReceivers?.() || [];
    for (const receiver of receivers) {
        if (!receiver?.track || _jitterTargetApplied.has(receiver))
            continue;
        try {
            if ('jitterBufferTarget' in receiver) {
                receiver.jitterBufferTarget = JITTER_BUFFER_TARGET_MS; // ms (Chrome 110+)
            }
            else if ('playoutDelayHint' in receiver) {
                receiver.playoutDelayHint = JITTER_BUFFER_TARGET_MS / 1000; // 초 (구버전)
            }
            _jitterTargetApplied.add(receiver);
        }
        catch (err) {
            // 미지원 브라우저는 조용히 무시
        }
    }
}
/**
 * 현재 유효 수신 상한 (디바이스 기본 캡과 강등값 중 낮은 쪽)
 * @returns {number}
 */
function _currentCap() {
    const st = APP.store.getState()['features/video-layout'];
    const deviceCap = getDefaultReceiveMaxHeight();
    return st.receiveMaxHeight ? Math.min(deviceCap, st.receiveMaxHeight) : deviceCap;
}
async function _checkReceiveHealth(conference) {
    const pc = conference?.getActiveMediaSession?.()?.peerconnection?.peerconnection;
    if (!pc)
        return;
    _applyJitterBufferTarget(pc);
    let decoded = 0;
    let dropped = 0;
    let decodeTime = 0; // 스테이지 전 비디오 스트림의 누적 디코드 시간(초) 합
    let freezes = 0;
    let freezeDuration = 0; // 누적 프리즈 시간(초) 합
    const heights = new Map(); // ssrc → 현재 수신 해상도
    try {
        const stats = await pc.getStats();
        stats.forEach((stat) => {
            if (stat.type === 'inbound-rtp' && (stat.kind === 'video' || stat.mediaType === 'video')) {
                decoded += stat.framesDecoded || 0;
                dropped += stat.framesDropped || 0;
                decodeTime += stat.totalDecodeTime || 0;
                freezes += stat.freezeCount || 0;
                freezeDuration += stat.totalFreezesDuration || 0;
                heights.set(stat.ssrc, stat.frameHeight || 0);
            }
        });
    }
    catch (err) {
        return;
    }
    // 계층 전환 관측(진단 전용) — 이전 틱에도 있던 스트림의 해상도 변화만 로그.
    // 480p 미만 사이의 전환(핀 학생 타일의 180↔360 등)은 체감 영향이 작아 제외.
    let flapDetail = '';
    heights.forEach((h, ssrc) => {
        const prev = prevHeights.get(ssrc);
        if (prev !== undefined && prev > 0 && h > 0 && prev !== h && Math.max(prev, h) >= 480) {
            flapDetail += `${ssrc}: ${prev}→${h} `;
        }
    });
    prevHeights = heights;
    if (flapDetail)
        console.info(`[videoQuality] 계층 전환 감지 — ${flapDetail.trim()}`);
    const decodedDelta = decoded - prevDecoded;
    const droppedDelta = dropped - prevDropped;
    const decodeTimeDelta = decodeTime - prevDecodeTime;
    const freezeDelta = freezes - prevFreezes;
    const freezeDurationDelta = freezeDuration - prevFreezeDuration;
    prevDecoded = decoded;
    prevDropped = dropped;
    prevDecodeTime = decodeTime;
    prevFreezes = freezes;
    prevFreezeDuration = freezeDuration;
    // 미디어 세션 재시작 등으로 누적치가 리셋되면 이번 창은 버린다
    if (decodedDelta < 0 || droppedDelta < 0 || decodeTimeDelta < 0 || freezeDelta < 0 || freezeDurationDelta < 0)
        return;
    const total = decodedDelta + droppedDelta;
    const dropRatio = total > 0 ? droppedDelta / total : 0;
    // 프레임당 평균 디코드 시간(ms). 여러 스트림(강사+핀 학생) 합산 기준.
    const avgDecodeMs = decodedDelta > 0 ? (decodeTimeDelta / decodedDelta) * 1000 : 0;
    // 축 1: 디코드 과부하 — 드롭이 나면서(증상) 디코드 시간도 높을 때만(원인).
    // 충분한 프레임 표본이 있는 창에서만 판단한다 (부족하면 보류: 스트라이크 유지).
    if (total >= MIN_FRAMES_PER_WINDOW) {
        const decodeOverloaded = dropRatio > DROP_RATIO_THRESHOLD && avgDecodeMs > DECODE_TIME_MS_THRESHOLD;
        decodeStrikes = decodeOverloaded ? decodeStrikes + 1 : 0;
    }
    // 축 2: 프리즈 폭증 (BWE가 못 잡는 버스트성 손실) — 표본과 무관하게 항상 평가한다.
    // 전송이 통째로 멈추면 디코드 프레임이 0이라, 표본 게이트 뒤에 두면
    // 정작 프리즈가 쌓이는 구간에서 감시가 꺼진다 (실측 버그 수정).
    const freezeStorm = freezeDelta >= FREEZE_COUNT_THRESHOLD || freezeDurationDelta >= FREEZE_DURATION_S_THRESHOLD;
    freezeStrikes = freezeStorm ? freezeStrikes + 1 : 0;
    if (decodeStrikes < STRIKES_TO_DEGRADE && freezeStrikes < STRIKES_TO_DEGRADE)
        return;
    const cause = decodeStrikes >= STRIKES_TO_DEGRADE
        ? `디코드 과부하(드롭 ${(dropRatio * 100).toFixed(0)}%, ${avgDecodeMs.toFixed(1)}ms/frame)`
        : `프리즈 폭증(${freezeDelta}회·${freezeDurationDelta.toFixed(1)}s/5s — 버스트 손실 의심)`;
    // 현재 상한보다 한 계단 낮은 계층으로 강등
    const cap = _currentCap();
    const next = DEGRADE_TIERS.find(h => h < cap) ?? null;
    if (next === null) {
        // 이미 최하단(480p). 더 내려도 받을 계층이 없으므로 감시 종료.
        stopReceiveQualityMonitor();
        console.warn(`[videoQuality] ${cause} 지속이나 최하단(${DECODE_FLOOR}p) 도달 — 추가 강등 불가`);
        return;
    }
    decodeStrikes = 0; // 다음 계단 관측을 위해 리셋 (계속 감시)
    freezeStrikes = 0;
    console.warn(`[videoQuality] ${cause} → 수신 상한 ${cap}p→${next}p 강등`);
    APP.store.dispatch(setReceiveMaxHeight(next));
}
